import { Currency } from '@hourly/utilities/Currency';
import dayjs, { type Dayjs } from 'dayjs';

export const RevenueShareStatus = {
  open: 'Open',
  paid: 'Paid',
  submitted: 'Submitted',
} as const;
export type RevenueShareStatus = (typeof RevenueShareStatus)[keyof typeof RevenueShareStatus];

export class Percentage {
  private readonly _value: number;

  constructor(value: number) {
    this._value = value;
  }

  public get value(): number {
    return this._value;
  }

  public toString(): string {
    return `${(this._value * 100).toFixed(1)}%`;
  }

  valueOf(): number {
    return this._value;
  }
}

export interface RevenueShare {
  businessName: string;
  fee: Currency;
  percentage: Percentage;
  total: Currency;
}

export class RevenueSharePeriod {
  public id: string;
  public date: string;
  public status: RevenueShareStatus;
  public children: RevenueShare[] = [];
  public paymentDate: string;

  constructor(status: RevenueShareStatus, billedDate: Dayjs, paymentDate: Dayjs | null) {
    this.status = status;
    this.date = dayjs(billedDate).format('MMMM, YYYY');
    this.paymentDate = paymentDate ? paymentDate.format('MMMM, D') : '-';

    const billingDate = dayjs(billedDate);
    this.id = `${billingDate.format('YYYY-MM')}-${this.status}`;
  }

  add(revenueShare: RevenueShare) {
    this.children.push(revenueShare);
  }

  total() {
    const totalValue = this.children.reduce((sum, entry) => sum + entry.total.value, 0);
    return new Currency(totalValue);
  }

  filterBy(searchTerm: string): RevenueShare[] {
    if (!searchTerm) return this.children;

    return this.children.filter(({ businessName }) =>
      businessName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
}

export class RevenueShareLedger {
  private _map = new Map<string, RevenueSharePeriod>();

  private genKey(status: RevenueShareStatus, period: Dayjs) {
    const billingDate = dayjs(period).format('MMMM, YYYY');
    return `${billingDate}-${status}`;
  }

  add(
    status: RevenueShareStatus,
    businessName: string,
    fee: number,
    percentage: number,
    total: number,
    billedDate: Dayjs,
    paymentDate: Dayjs | null
  ): void {
    const period =
      this._map.get(this.genKey(status, billedDate)) ||
      new RevenueSharePeriod(status, billedDate, paymentDate);

    period.add({
      businessName,
      fee: new Currency(fee),
      percentage: new Percentage(percentage),
      total: new Currency(total),
    });
    this._map.set(this.genKey(status, billedDate), period);
  }

  hasPeriods(): boolean {
    return this._map.size > 0;
  }

  periods(): RevenueSharePeriod[] {
    return Array.from(this._map.values()).sort((a, b) => b.id.localeCompare(a.id));
  }

  latestPeriod(): RevenueSharePeriod | null {
    const periods = this.periods();
    return this.hasPeriods() ? periods[0] : null;
  }
}
